import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  Grid,
  Tab,
  Tabs,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  TextField,
  InputAdornment,
  Avatar,
  Snackbar,
  AlertTitle,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  History as HistoryIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
// DatePicker temporarily disabled due to dependency conflicts
// import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import apiService from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reports-tabpanel-${index}`}
      aria-labelledby={`reports-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Reports: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  
  // Refs for cleanup
  const previousTabValue = useRef<number>(0);
  const isUnmounting = useRef<boolean>(false);
  
  // Daily History State
  const [dailyHistory, setDailyHistory] = useState<any[]>([]);
  const [dailyTrends, setDailyTrends] = useState<any>(null);
  const [peakHours, setPeakHours] = useState<any[]>([]);
  const [todayStats, setTodayStats] = useState<any>(null);
  
  // Audit Log State
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [auditStats, setAuditStats] = useState<any>(null);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotalPages, setAuditTotalPages] = useState(1);
  
  // Check-in History State
  const [checkInHistory, setCheckInHistory] = useState<any[]>([]);
  const [checkInPage, setCheckInPage] = useState(1);
  const [checkInTotalPages, setCheckInTotalPages] = useState(1);
  const [checkInSearch, setCheckInSearch] = useState('');
  const [checkInFilters, setCheckInFilters] = useState({
    status: '',
    deviceType: '',
    membershipType: ''
  });
  
  // Filters
  const [startDate, setStartDate] = useState<Date>(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [trendDays, setTrendDays] = useState(30);
  const [auditFilters, setAuditFilters] = useState({
    action: '',
    resourceType: '',
    status: '',
    userId: ''
  });

  // Clear filters when switching tabs or unmounting
  const clearFiltersForTab = useCallback((tabIndex: number) => {
    switch (tabIndex) {
      case 1: // Audit Logs
        setAuditFilters({
          action: '',
          resourceType: '',
          status: '',
          userId: ''
        });
        setAuditPage(1);
        break;
      case 2: // Check-in History
        setCheckInSearch('');
        setCheckInFilters({
          status: '',
          deviceType: '',
          membershipType: ''
        });
        setCheckInPage(1);
        break;
      default:
        // For daily reports, we might want to keep date filters
        break;
    }
  }, []);

  // Reset all filters to initial state
  const resetAllFilters = useCallback(() => {
    setCheckInSearch('');
    setCheckInFilters({
      status: '',
      deviceType: '',
      membershipType: ''
    });
    setAuditFilters({
      action: '',
      resourceType: '',
      status: '',
      userId: ''
    });
    setStartDate(subDays(new Date(), 30));
    setEndDate(new Date());
    setTrendDays(30);
    setAuditPage(1);
    setCheckInPage(1);
  }, []);

  // Error handling utilities
  const handleError = useCallback((err: any, context: string) => {
    const errorMessage = err.response?.data?.message || err.message || `Failed to ${context}`;
    setError(errorMessage);
    setShowErrorSnackbar(true);
    console.error(`Error ${context}:`, err);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
    setShowErrorSnackbar(false);
  }, []);

  const loadDailyReports = useCallback(async () => {
    if (isUnmounting.current) return;
    
    try {
      setLoading(true);
      clearError();
      
      const [historyResponse, trendsResponse, peakHoursResponse, todayResponse] = await Promise.all([
        apiService.getDailyHistory({
          startDate: startOfDay(startDate).toISOString(),
          endDate: endOfDay(endDate).toISOString(),
          limit: 50
        }),
        apiService.getDailyTrends(trendDays),
        apiService.get('/api/daily-history/peak-hours', { params: { days: trendDays } }),
        apiService.get('/api/daily-history/today')
      ]);
      
      if (!isUnmounting.current) {
        setDailyHistory(historyResponse.dailyHistory || []);
        setDailyTrends(trendsResponse.trends || null);
        setPeakHours(peakHoursResponse.peakHours || []);
        setTodayStats(todayResponse.realTimeStats || null);
      }
    } catch (err: any) {
      if (!isUnmounting.current) {
        handleError(err, 'load daily reports');
        // Set empty states on error
        setDailyHistory([]);
        setDailyTrends(null);
        setPeakHours([]);
        setTodayStats(null);
      }
    } finally {
      if (!isUnmounting.current) {
        setLoading(false);
      }
    }
  }, [startDate, endDate, trendDays, handleError, clearError]);

  const loadAuditReports = useCallback(async () => {
    if (isUnmounting.current) return;
    
    try {
      setLoading(true);
      clearError();
      
      const [logsResponse, statsResponse] = await Promise.all([
        apiService.get('/api/audit', {
          params: {
            page: auditPage,
            limit: 20,
            startDate: startOfDay(startDate).toISOString(),
            endDate: endOfDay(endDate).toISOString(),
            ...auditFilters
          }
        }),
        apiService.get('/api/audit/stats', { params: { days: trendDays } })
      ]);
      
      if (!isUnmounting.current) {
        setAuditLogs(logsResponse.auditLogs || []);
        setAuditTotalPages(logsResponse.pagination?.pages || 1);
        setAuditStats(statsResponse);
      }
    } catch (err: any) {
      if (!isUnmounting.current) {
        handleError(err, 'load audit reports');
      }
    } finally {
      if (!isUnmounting.current) {
        setLoading(false);
      }
    }
  }, [startDate, endDate, trendDays, auditPage, auditFilters, handleError, clearError]);

  const loadCheckInHistory = useCallback(async () => {
    if (isUnmounting.current) return;
    
    try {
      setLoading(true);
      clearError();
      
      const response = await apiService.getCheckIns({
        page: checkInPage,
        limit: 25,
        search: checkInSearch || undefined,
        status: checkInFilters.status || undefined,
        deviceType: checkInFilters.deviceType || undefined,
        startDate: startOfDay(startDate).toISOString(),
        endDate: endOfDay(endDate).toISOString()
      });
      
      if (!isUnmounting.current) {
        setCheckInHistory(response.checkIns || []);
        setCheckInTotalPages(response.pagination?.pages || 1);
      }
    } catch (err: any) {
      if (!isUnmounting.current) {
        handleError(err, 'load check-in history');
      }
    } finally {
      if (!isUnmounting.current) {
        setLoading(false);
      }
    }
  }, [startDate, endDate, checkInPage, checkInSearch, checkInFilters, handleError, clearError]);

  // Retry functionality for failed operations
  const retryCurrentOperation = useCallback(() => {
    clearError();
    if (tabValue === 0) loadDailyReports();
    else if (tabValue === 1) loadAuditReports();
    else if (tabValue === 2) loadCheckInHistory();
  }, [tabValue, loadDailyReports, loadAuditReports, loadCheckInHistory, clearError]);

  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      isUnmounting.current = true;
      resetAllFilters();
    };
  }, [resetAllFilters]);

  // Effect to clear filters when switching tabs
  useEffect(() => {
    if (previousTabValue.current !== tabValue) {
      clearFiltersForTab(previousTabValue.current);
      clearError();
      previousTabValue.current = tabValue;
    }
  }, [tabValue, clearFiltersForTab, clearError]);

  useEffect(() => {
    if (tabValue === 0) {
      loadDailyReports();
    } else if (tabValue === 1) {
      loadAuditReports();
    } else if (tabValue === 2) {
      loadCheckInHistory();
    }
  }, [tabValue, loadDailyReports, loadAuditReports, loadCheckInHistory]);

  // Debounced search effect for check-in history
  useEffect(() => {
    if (tabValue === 2) {
      const timeoutId = setTimeout(() => {
        setCheckInPage(1); // Reset to first page when search changes
        loadCheckInHistory();
      }, 500); // 500ms delay for debounced search

      return () => clearTimeout(timeoutId);
    }
  }, [checkInSearch, checkInFilters, tabValue, loadCheckInHistory]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    // Clear any existing errors when switching tabs
    clearError();
    setTabValue(newValue);
  };

  const handleExportDaily = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/api/daily-history/export/csv', {
        params: {
          startDate: startOfDay(startDate).toISOString(),
          endDate: endOfDay(endDate).toISOString()
        },
        responseType: 'blob'
      });
      
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `daily-checkin-history-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      handleError(err, 'export daily history');
    } finally {
      setLoading(false);
    }
  };

  const handleExportAudit = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/api/audit/export/csv', {
        params: {
          startDate: startOfDay(startDate).toISOString(),
          endDate: endOfDay(endDate).toISOString()
        },
        responseType: 'blob'
      });
      
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-logs-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      handleError(err, 'export audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCheckIns = async () => {
    try {
      setLoading(true);
      // Since there's no specific check-in export endpoint, we'll create our own CSV
      const allCheckIns = await apiService.getCheckIns({
        limit: 10000, // Get all check-ins for the date range
        search: checkInSearch || undefined,
        status: checkInFilters.status || undefined,
        deviceType: checkInFilters.deviceType || undefined,
        startDate: startOfDay(startDate).toISOString(),
        endDate: endOfDay(endDate).toISOString()
      });
      
      // Create CSV content
      const headers = ['Date', 'Time', 'Member Name', 'Member ID', 'Status', 'Device Type', 'Device ID', 'Membership Type', 'Notes'];
      const csvContent = [
        headers.join(','),
        ...allCheckIns.checkIns.map((checkIn: any) => [
          format(new Date(checkIn.timestamp), 'yyyy-MM-dd'),
          format(new Date(checkIn.timestamp), 'HH:mm:ss'),
          `"${checkIn.member.firstName} ${checkIn.member.lastName}"`,
          checkIn.member.memberId,
          checkIn.status,
          checkIn.deviceType,
          checkIn.deviceId,
          checkIn.member.membershipType || '',
          `"${checkIn.notes || ''}"`
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `checkin-history-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      handleError(err, 'export check-in history');
    } finally {
      setLoading(false);
    }
  };

  const formatChartData = (data: any[]) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }
    
    return data.map(item => ({
      date: format(new Date(item.date), 'MMM dd'),
      checkIns: item.totalCheckIns || 0,
      uniqueMembers: item.uniqueMembers || 0
    }));
  };

  return (
    <Box sx={{ 
      background: 'linear-gradient(135deg, #f9fafb 0%, #fafbfc 100%)',
      minHeight: 'calc(100vh - 170px)',
      color: '#1a202c'
    }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1a202c' }}>
            Reports & Analytics
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                if (tabValue === 0) loadDailyReports();
                else if (tabValue === 1) loadAuditReports();
                else if (tabValue === 2) loadCheckInHistory();
              }}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<FilterListIcon />}
              onClick={resetAllFilters}
              disabled={loading}
            >
              Clear All Filters
            </Button>
          </Box>
        </Box>

        {/* Error Display */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
            onClose={clearError}
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button color="inherit" size="small" onClick={retryCurrentOperation} disabled={loading}>
                  Retry
                </Button>
                <Button color="inherit" size="small" onClick={clearError}>
                  Dismiss
                </Button>
              </Box>
            }
          >
            <AlertTitle>Error</AlertTitle>
            {error}
          </Alert>
        )}

        {/* Error Snackbar */}
        <Snackbar
          open={showErrorSnackbar}
          autoHideDuration={6000}
          onClose={() => setShowErrorSnackbar(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert 
            onClose={() => setShowErrorSnackbar(false)} 
            severity="error" 
            sx={{ width: '100%' }}
          >
            {error}
          </Alert>
        </Snackbar>

        {/* Date Range Filters */}
        <Paper sx={{ 
          p: 3, 
          mb: 3,
          background: 'linear-gradient(135deg, #fafbfc 0%, #f7fafc 100%)',
          border: '2px solid #d1d9e6',
          borderRadius: 3,
          boxShadow: '0 6px 28px rgba(0, 0, 0, 0.15)',
        }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#1a202c', fontWeight: 600 }}>
            Date Range & Filters
          </Typography>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Start Date"
                type="date"
                value={format(startDate, 'yyyy-MM-dd')}
                onChange={(e) => setStartDate(new Date(e.target.value))}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="End Date"
                type="date"
                value={format(endDate, 'yyyy-MM-dd')}
                onChange={(e) => setEndDate(new Date(e.target.value))}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Trend Period</InputLabel>
                <Select
                  value={trendDays}
                  label="Trend Period"
                  onChange={(e) => setTrendDays(Number(e.target.value))}
                >
                  <MenuItem value={7}>Last 7 days</MenuItem>
                  <MenuItem value={30}>Last 30 days</MenuItem>
                  <MenuItem value={90}>Last 3 months</MenuItem>
                  <MenuItem value={365}>Last year</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabs */}
        <Paper sx={{ 
          background: 'linear-gradient(135deg, #ffffff 0%, #f7fafc 100%)',
          border: '2px solid #d1d9e6',
          borderRadius: 3,
          boxShadow: '0 6px 28px rgba(0, 0, 0, 0.15)',
        }}>
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab 
              label="Daily Check-in Reports" 
              icon={<AssessmentIcon />} 
              iconPosition="start"
              sx={{ fontWeight: 600 }}
            />
            <Tab 
              label="Audit & Activity Logs" 
              icon={<SecurityIcon />} 
              iconPosition="start"
              sx={{ fontWeight: 600 }}
            />
            <Tab 
              label="Check-in History" 
              icon={<HistoryIcon />} 
              iconPosition="start"
              sx={{ fontWeight: 600 }}
            />
          </Tabs>

          {/* Daily Reports Tab */}
          <TabPanel value={tabValue} index={0}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                {/* Today's Stats Summary */}
                {todayStats && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ color: '#1a202c', fontWeight: 600 }}>
                      Today's Real-time Statistics
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={6} sm={3}>
                        <Card sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h4" color="primary">
                            {todayStats.totalCheckIns}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Check-ins
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Card sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h4" color="success.main">
                            {todayStats.uniqueMembers}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Unique Members
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Card sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h4" color="success.main">
                            {todayStats.checkInsByStatus.success}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Successful
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Card sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h4" color="error.main">
                            {todayStats.checkInsByStatus.contractExpired + 
                             todayStats.checkInsByStatus.paymentOverdue + 
                             todayStats.checkInsByStatus.suspended}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Failed
                          </Typography>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                {/* Trends Chart */}
                {dailyTrends && dailyTrends.trends && dailyTrends.trends.daily && dailyTrends.trends.daily.length > 0 && (
                  <Grid item xs={12} lg={8}>
                    <Card sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Check-in Trends
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={handleExportDaily}
                        >
                          Export CSV
                        </Button>
                      </Box>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={formatChartData(dailyTrends.trends.daily)}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <RechartsTooltip />
                          <Legend />
                          <Line 
                            type="monotone" 
                            dataKey="checkIns" 
                            stroke="#1976d2" 
                            strokeWidth={2}
                            name="Total Check-ins"
                          />
                          <Line 
                            type="monotone" 
                            dataKey="uniqueMembers" 
                            stroke="#4caf50" 
                            strokeWidth={2}
                            name="Unique Members"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Card>
                  </Grid>
                )}

                {/* Peak Hours Chart */}
                {peakHours.length > 0 && (
                  <Grid item xs={12} lg={4}>
                    <Card sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Peak Hours (Avg)
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={peakHours}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="hour" />
                          <YAxis />
                          <RechartsTooltip />
                          <Bar dataKey="averageCheckIns" fill="#1976d2" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card>
                  </Grid>
                )}

                {/* Daily History Table */}
                <Grid item xs={12}>
                  <Card sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Daily History
                    </Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'background.default' }}>
                            <TableCell><strong>Date</strong></TableCell>
                            <TableCell><strong>Total Check-ins</strong></TableCell>
                            <TableCell><strong>Unique Members</strong></TableCell>
                            <TableCell><strong>Success Rate</strong></TableCell>
                            <TableCell><strong>Peak Hour</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dailyHistory && dailyHistory.length > 0 ? dailyHistory.map((day) => {
                            const successRate = day.totalCheckIns > 0 
                              ? Math.round(((day.checkInsByStatus?.success || 0) / day.totalCheckIns) * 100)
                              : 0;
                            const peakHour = day.peakHours && day.peakHours.length > 0 
                              ? day.peakHours.reduce((prev: any, current: any) => 
                                  prev.count > current.count ? prev : current
                                )
                              : null;
                            
                            return (
                              <TableRow key={day._id || day.date}>
                                <TableCell>
                                  {format(new Date(day.date), 'MMM dd, yyyy')}
                                </TableCell>
                                <TableCell>{day.totalCheckIns || 0}</TableCell>
                                <TableCell>{day.uniqueMembers || 0}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={`${successRate}%`}
                                    color={successRate >= 90 ? 'success' : successRate >= 70 ? 'warning' : 'error'}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  {peakHour ? `${peakHour.hour}:00 (${peakHour.count})` : 'N/A'}
                                </TableCell>
                              </TableRow>
                            );
                          }) : (
                            <TableRow>
                              <TableCell colSpan={5} align="center">
                                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                  No daily history data available for the selected date range
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Card>
                </Grid>
              </Grid>
            )}
          </TabPanel>

          {/* Audit Logs Tab */}
          <TabPanel value={tabValue} index={1}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                {/* Audit Stats Summary */}
                {auditStats && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ color: '#1a202c', fontWeight: 600 }}>
                      Activity Summary
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={6} sm={3}>
                        <Card sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h4" color="primary">
                            {auditStats.quickStats?.totalActions || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Actions
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Card sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h4" color="success.main">
                            {auditStats.quickStats?.successfulActions || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Successful
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Card sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h4" color="error.main">
                            {auditStats.quickStats?.failedActions || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Failed
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Card sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h4" color="info.main">
                            {auditStats.quickStats?.uniqueUsersCount || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Active Users
                          </Typography>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                {/* Audit Filters */}
                <Grid item xs={12}>
                  <Card sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Audit Log Filters
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Action</InputLabel>
                          <Select
                            value={auditFilters.action}
                            label="Action"
                            onChange={(e) => setAuditFilters(prev => ({ ...prev, action: e.target.value }))}
                          >
                            <MenuItem value="">All Actions</MenuItem>
                            <MenuItem value="member:create">Member Created</MenuItem>
                            <MenuItem value="member:update">Member Updated</MenuItem>
                            <MenuItem value="member:delete">Member Deleted</MenuItem>
                            <MenuItem value="payment:create">Payment Created</MenuItem>
                            <MenuItem value="checkin:create">Check-in Created</MenuItem>
                            <MenuItem value="auth:login">User Login</MenuItem>
                            <MenuItem value="auth:logout">User Logout</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Resource</InputLabel>
                          <Select
                            value={auditFilters.resourceType}
                            label="Resource"
                            onChange={(e) => setAuditFilters(prev => ({ ...prev, resourceType: e.target.value }))}
                          >
                            <MenuItem value="">All Resources</MenuItem>
                            <MenuItem value="Member">Members</MenuItem>
                            <MenuItem value="Payment">Payments</MenuItem>
                            <MenuItem value="CheckIn">Check-ins</MenuItem>
                            <MenuItem value="User">Users</MenuItem>
                            <MenuItem value="Auth">Authentication</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Status</InputLabel>
                          <Select
                            value={auditFilters.status}
                            label="Status"
                            onChange={(e) => setAuditFilters(prev => ({ ...prev, status: e.target.value }))}
                          >
                            <MenuItem value="">All Status</MenuItem>
                            <MenuItem value="success">Success</MenuItem>
                            <MenuItem value="failed">Failed</MenuItem>
                            <MenuItem value="partial">Partial</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={handleExportAudit}
                        >
                          Export CSV
                        </Button>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>

                {/* Audit Logs Table */}
                <Grid item xs={12}>
                  <Card sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Audit Logs
                    </Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'background.default' }}>
                            <TableCell><strong>Time</strong></TableCell>
                            <TableCell><strong>User</strong></TableCell>
                            <TableCell><strong>Action</strong></TableCell>
                            <TableCell><strong>Resource</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Details</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {auditLogs.map((log) => (
                            <TableRow key={log._id}>
                              <TableCell>
                                {format(new Date(log.createdAt), 'MMM dd, HH:mm:ss')}
                              </TableCell>
                              <TableCell>
                                {log.userId?.firstName || 'System'} {log.userId?.lastName || ''}
                                <br />
                                <Typography variant="caption" color="text.secondary">
                                  {log.userId?.username || 'system'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={log.action} 
                                  size="small"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>{log.resourceType}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={log.status}
                                  color={log.status === 'success' ? 'success' : 'error'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                  {JSON.stringify(log.details)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {/* Pagination */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                      <Pagination
                        count={auditTotalPages}
                        page={auditPage}
                        onChange={(_, page) => setAuditPage(page)}
                        color="primary"
                      />
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            )}
          </TabPanel>

          {/* Check-in History Tab */}
          <TabPanel value={tabValue} index={2}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                {/* Search and Filters */}
                <Grid item xs={12}>
                  <Card sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      <FilterListIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
                      Search & Filter Check-ins
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Search members"
                          placeholder="Name, Member ID, or Membership Type"
                          value={checkInSearch}
                          onChange={(e) => setCheckInSearch(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Status</InputLabel>
                          <Select
                            value={checkInFilters.status}
                            label="Status"
                            onChange={(e) => setCheckInFilters(prev => ({ ...prev, status: e.target.value }))}
                          >
                            <MenuItem value="">All Status</MenuItem>
                            <MenuItem value="success">Success</MenuItem>
                            <MenuItem value="contract-expired">Contract Expired</MenuItem>
                            <MenuItem value="payment-overdue">Payment Overdue</MenuItem>
                            <MenuItem value="suspended">Suspended</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Device Type</InputLabel>
                          <Select
                            value={checkInFilters.deviceType}
                            label="Device Type"
                            onChange={(e) => setCheckInFilters(prev => ({ ...prev, deviceType: e.target.value }))}
                          >
                            <MenuItem value="">All Devices</MenuItem>
                            <MenuItem value="tablet">Gym Tablet</MenuItem>
                            <MenuItem value="kiosk">Self-Service Kiosk</MenuItem>
                            <MenuItem value="admin">Admin System</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={() => {
                            setCheckInSearch('');
                            setCheckInFilters({ status: '', deviceType: '', membershipType: '' });
                          }}
                        >
                          Clear Filters
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={handleExportCheckIns}
                        >
                          Export CSV
                        </Button>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>

                {/* Check-ins Table */}
                <Grid item xs={12}>
                  <Card sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Check-in History ({checkInHistory.length} records)
                    </Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'background.default' }}>
                            <TableCell><strong>Member</strong></TableCell>
                            <TableCell><strong>Date & Time</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Device</strong></TableCell>
                            <TableCell><strong>Membership</strong></TableCell>
                            <TableCell><strong>Notes</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {checkInHistory.map((checkIn) => (
                            <TableRow 
                              key={checkIn._id}
                              sx={{
                                bgcolor: checkIn.status !== 'success' ? 'error.light' : 'inherit',
                                '&:hover': {
                                  bgcolor: checkIn.status !== 'success' ? 'error.main' : 'action.hover',
                                }
                              }}
                            >
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Avatar
                                    src={checkIn.member?.profileImage || undefined}
                                    sx={{ width: 32, height: 32 }}
                                  >
                                    {checkIn.member?.firstName?.[0]}{checkIn.member?.lastName?.[0]}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" fontWeight="medium">
                                      {checkIn.member?.firstName} {checkIn.member?.lastName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {checkIn.member?.memberId}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {format(new Date(checkIn.timestamp), 'MMM dd, yyyy')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {format(new Date(checkIn.timestamp), 'HH:mm:ss')}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={
                                    checkIn.status === 'success' ? 'Success' :
                                    checkIn.status === 'contract-expired' ? 'Expired' :
                                    checkIn.status === 'payment-overdue' ? 'Overdue' :
                                    checkIn.status === 'suspended' ? 'Suspended' : 
                                    checkIn.status
                                  }
                                  color={checkIn.status === 'success' ? 'success' : 'error'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2">
                                    {checkIn.deviceType === 'tablet' ? 'Gym Tablet' : 
                                     checkIn.deviceType === 'kiosk' ? 'Self-Service' : 
                                     'Admin System'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {checkIn.deviceId}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={checkIn.member?.membershipType || 'Unknown'}
                                  variant="outlined"
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ maxWidth: 150 }} noWrap>
                                  {checkIn.notes || '-'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {checkInHistory.length === 0 && (
                      <Box textAlign="center" py={4}>
                        <Typography variant="body1" color="text.secondary">
                          No check-ins found for the selected criteria
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mt={1}>
                          Try adjusting your search terms or date range
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Pagination */}
                    {checkInTotalPages > 1 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <Pagination
                          count={checkInTotalPages}
                          page={checkInPage}
                          onChange={(_, page) => setCheckInPage(page)}
                          color="primary"
                        />
                      </Box>
                    )}
                  </Card>
                </Grid>
              </Grid>
            )}
          </TabPanel>
        </Paper>
      </Box>
  );
};

export default Reports;
